import { message } from 'antd'
import type { ApiError, AssetChainOut } from '../../api/client'
import { ensureI18nInitialized } from '../../i18n/config'

/**
 * VISTOERA_IMAGE_EDITOR_ASYNC_SAVE_AND_WHEEL_ISOLATION_P0
 * + TASK-00611 save-state consistency
 *
 * Module-level (component-independent) save task registry. An editor hands
 * its fully-prepared payload to `submitEditorSave` and may then unmount
 * immediately — the upload/persist promise chain is owned by this module,
 * never by the editor component, so closing the modal can never abort it.
 *
 * Lifecycle: IDLE → IN_FLIGHT (dedupe + handoff) → SUCCEEDED | FAILED.
 * - SUCCEEDED: `onSaved` fires so the list row merges the fresh chain.
 * - FAILED: global antd toast + `onFailure` so an still-mounted editor can
 *   restore dirty/retry (never leave "保存中 / 后台继续" stuck after a real
 *   failure). `inFlight` always clears so a later click can retry.
 */

export type EditorSaveKind = 'transparent' | 'background' | 'shadow'

export interface EditorSaveTask {
  kind: EditorSaveKind
  chainId: string
  run: () => Promise<AssetChainOut | void>
  onSaved?: (chain: AssetChainOut) => void
  /** Fired when the background save rejects — editor may still be open. */
  onFailure?: (error: Error) => void
  successMessage?: string
}

const inFlight = new Set<string>()

function keyOf(kind: EditorSaveKind, chainId: string) {
  return `${kind}:${chainId}`
}

/** Map opaque axios / gateway failures to a short actionable toast. */
export function formatEditorSaveError(err: unknown): string {
  const i18n = ensureI18nInitialized()
  const e = err as ApiError | Error | undefined
  const status = (e as ApiError | undefined)?.status
  const code = (e as ApiError | undefined)?.code
  const raw = typeof e?.message === 'string' ? e.message.trim() : ''

  if ((e as ApiError | undefined)?.canceled) {
    return i18n.t('autoProcess:cutoutEditor.saveFailed')
  }
  if (code === 'ECONNABORTED' || /timeout/i.test(raw)) {
    return i18n.t('autoProcess:chainEditor.saveFailedTimeout')
  }
  if (status === 401 || status === 403) {
    return i18n.t('autoProcess:chainEditor.saveFailedAuth')
  }
  if (status === 502 || status === 503 || status === 504) {
    return i18n.t('autoProcess:chainEditor.saveFailedUnavailable')
  }
  // Opaque "Network Error" (no HTTP response — often a gateway 502 page
  // without CORS) must not be shown verbatim; users read it as "offline".
  if (status === undefined && (/network error/i.test(raw) || raw === '' || code === 'ERR_NETWORK')) {
    return i18n.t('autoProcess:chainEditor.saveFailedNetwork')
  }
  if (raw && !/^network error$/i.test(raw)) return raw
  return i18n.t('autoProcess:cutoutEditor.saveFailed')
}

/**
 * Returns true when the task was accepted (caller may close the editor),
 * false when an identical save is already in flight (duplicate click).
 */
export function submitEditorSave(task: EditorSaveTask): boolean {
  const key = keyOf(task.kind, task.chainId)
  if (inFlight.has(key)) return false
  inFlight.add(key)
  ;(async () => {
    try {
      const data = await task.run()
      if (data) task.onSaved?.(data)
      if (task.successMessage) message.success(task.successMessage)
    } catch (e) {
      const formatted = formatEditorSaveError(e)
      message.error(formatted)
      try {
        task.onFailure?.(e instanceof Error ? e : new Error(formatted))
      } catch {
        // UI reset must never mask the original failure toast.
      }
    } finally {
      inFlight.delete(key)
    }
  })()
  return true
}

export function isEditorSaveInFlight(kind: EditorSaveKind, chainId: string) {
  return inFlight.has(keyOf(kind, chainId))
}
